import { Component, HostListener, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { environment } from '../../environments/environment';

import { AuthQuery } from '@app/auth/auth.query';
import { AuthDataService } from '@app/auth/auth-data.service';
import { EventConfigService } from '@app/services/event-config.service';
import { EVENT_PHASES, DEFAULT_PHASE_ID, EventPhase } from '@app/event-config/event-phases';

/**
 * Dashboard shell. Hosts the header (logo, superuser cog + badge, live system,
 * profile menu), a primary <router-outlet> for the main area (DashboardHome /
 * EventConfig), and the slide-over sidenav bound to the named `detail` outlet
 * (visitor/exhibitor edit pages).
 */
@Component({
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  // content values
  _txt_subline = environment.txt_Subline;
  _txt_event_domain = environment.txt_Event_Domain;

  // profile menu
  _userName = this.authQuery.name;
  _userEmail = this.authQuery.email;
  _profileImage = this.authQuery.getValue().profile_image;
  profileMenuOpen = false;
  mobileMenuOpen = false;

  // slide-over detail outlet state
  detailOpen = false;

  // Current event lifecycle phase id (loaded from the read-only /v3/phase
  // endpoint, available to all backend users). Defaults to the live phase so
  // the header dot stays green until the value arrives.
  activePhaseId: string = DEFAULT_PHASE_ID;

  get isSuperuser() {
    return this.authQuery.isSuperuser;
  }

  /** The resolved phase definition (label, color, tooltip). */
  get activePhase(): EventPhase {
    return (
      EVENT_PHASES.find((p) => p.id === this.activePhaseId) ||
      EVENT_PHASES.find((p) => p.id === DEFAULT_PHASE_ID) ||
      EVENT_PHASES[0]
    );
  }

  get activePhaseColor(): string {
    return this.activePhase.color;
  }

  get activePhaseLabel(): string {
    return this.activePhase.label;
  }

  get activePhaseTip(): string {
    return this.activePhase.tip;
  }

  /** Human-readable role for the profile dropdown badge. */
  get roleLabel(): string {
    if (this.authQuery.su == 1) return 'Superuser';
    switch (this.authQuery.role) {
      case 1:
        return 'Admin';
      case 2:
        return 'Exhibitor';
      case 3:
        return 'Visitor';
      default:
        return 'User';
    }
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authQuery: AuthQuery,
    private authDataService: AuthDataService,
    private configService: EventConfigService
  ) {}

  ngOnInit(): void {
    this.configService.getPhase().subscribe({
      next: (res) => {
        if (res && res.phase) this.activePhaseId = res.phase;
      },
      error: () => {
        // keep the default phase on failure
      },
    });

    // Live-update the header badge + Live System dot when a phase is saved
    // from Event Configuration (no page reload needed).
    this.configService.phaseChanged$.subscribe((phase) => {
      if (phase) this.activePhaseId = phase;
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const t = event.target as HTMLElement;
    if (!t.closest('.profile-menu-container')) this.profileMenuOpen = false;
    if (!t.closest('.mobile-menu-container')) this.mobileMenuOpen = false;
  }

  toggleProfileMenu() {
    this.profileMenuOpen = !this.profileMenuOpen;
  }

  openEventConfig() {
    this.router.navigate(['/home/event-config']);
  }

  // close the slide-over by clearing the named detail outlet
  closeDetail() {
    this.router.navigate([{ outlets: { detail: null } }], { relativeTo: this.route });
  }

  logout() {
    this.authDataService.logout().subscribe(() => {
      this.router.navigate(['/atout']);
    });
  }

  gotoLiveSystem() {
    // set the form params
    const access_token = this.authQuery.access_token;
    const userid = this.authQuery.userid;
    const fid = this.authQuery.fid;
    const role = this.authQuery.role;

    const myform = document.createElement('form');
    myform.method = 'POST';
    myform.action = environment.eplusSystem + '/belogin.php';
    myform.style.display = 'none';

    const inputAuth = document.createElement('input');
    inputAuth.type = 'hidden';
    inputAuth.name = 'Authorization';
    inputAuth.value = access_token;
    myform.appendChild(inputAuth);

    const inputUid = document.createElement('input');
    inputUid.type = 'hidden';
    inputUid.name = 'uid';
    inputUid.value = '' + userid;
    myform.appendChild(inputUid);

    const inputFid = document.createElement('input');
    inputFid.type = 'hidden';
    inputFid.name = 'fid';
    inputFid.value = '' + fid;
    myform.appendChild(inputFid);

    const inputRole = document.createElement('input');
    inputRole.type = 'hidden';
    inputRole.name = 'role';
    inputRole.value = '' + role;
    myform.appendChild(inputRole);

    document.body.appendChild(myform);
    myform.target = '_blank';
    myform.submit();
  }
}
